import _ from "lodash";
import moment from "moment";
import {
  getDateTimeDifference,
  getIncrementedDate,
  getTodaysDate,
} from "Utils/DateTimeHelpers";

// * Utils
const isEnd = event => event?.eventType === "END";
const isStart = event => event?.eventType === "START";
const isReopen = event => event?.eventType === "REOPEN";
const isAddExtraTime = event => event?.eventType === "ADD_EXTRA_TIME";
const isWorksheetPause = event => event?.eventType === "WORKSHEET_PAUSE";
const isWorksheetUnpause = event => event?.eventType === "WORKSHEET_UNPAUSE";
const isRemoveExtraTime = event => event?.eventType === "REMOVE_ALL_EXTRA_TIME";

// * Extra Time Calculation from Events
export const getStudentExtraTime = _events => {
  const events = _.orderBy(_events, ["eventTime"], ["asc"]);
  let extraTime = 0;
  _.forEach(events, event => {
    if (isAddExtraTime(event)) {
      const duration = event?.duration;
      extraTime += isNaN(duration) ? 0 : _.toNumber(duration);
    } else if (isRemoveExtraTime(event) || isReopen(event)) {
      extraTime = 0;
    }
  });
  return extraTime;
};

// * Worksheet Pause State Calculation from Events
export const getStudentWorksheetPauseState = (_events, data) => {
  const events = _.orderBy(_events, ["eventTime"], ["asc"]);
  let totalPausedDuration = 0;
  let isWorksheetPaused = false;
  let lastPauseTime = null;
  const closeSubmissionDate = _.get(
    data,
    "assignment.closeSubmissionDate",
    null
  );

  _.forEach(events, event => {
    if (isWorksheetPause(event)) {
      if (!isWorksheetPaused) {
        lastPauseTime = moment(event?.eventTime || 0);
        isWorksheetPaused = true;
      }
    } else if (isWorksheetUnpause(event)) {
      if (isWorksheetPaused && lastPauseTime) {
        totalPausedDuration += getDateTimeDifference({
          date2: lastPauseTime,
          differenceBy: "seconds",
          date1: event?.eventTime || 0,
        });
        lastPauseTime = null;
        isWorksheetPaused = false;
      }
    } else if (isStart(event) || isReopen(event)) {
      if (!closeSubmissionDate && !isReopen(event)) {
        totalPausedDuration = 0;
      }
      lastPauseTime = null;
      isWorksheetPaused = false;
    }
  });

  if (data && _.has(data, "submission.isPaused")) {
    isWorksheetPaused = _.get(data, "submission.isPaused");
  }

  // Handle the case when the worksheet is still paused
  let currentPausedDuration = null;
  if (isWorksheetPaused && lastPauseTime) {
    currentPausedDuration = getDateTimeDifference({
      date2: lastPauseTime,
      date1: getTodaysDate(),
      differenceBy: "seconds",
    });
  }

  return {
    isWorksheetPaused,
    totalPausedDuration,
    currentPausedDuration,
    pauseEventTime: lastPauseTime,
    pausedDuration: currentPausedDuration,
  };
};

// * Worksheet Reopen State Calculation from Events
export const getStudentWorksheetReopenState = (_events, data) => {
  let events = _.orderBy(_events, ["eventTime"], ["asc"]);
  const reopenEventIndex = _.findLastIndex(events, isReopen);
  if (reopenEventIndex !== -1) events = events.slice(reopenEventIndex);

  let reopenDuration = 0;
  let isReopened = false;
  let reopenEventTime = null;
  let totalReopenDuration = 0;
  let startTimeAfterReopen = null;
  let lastReopenEventTime = null;

  const {
    isWorksheetPaused,
    totalPausedDuration,
    pauseEventTime,
  } = getStudentWorksheetPauseState(events, data);

  _.forEach(events, event => {
    if (isReopen(event)) {
      // * REOPEN Event - setting isReopened to true and calculating initial reopen duration
      isReopened = true;
      reopenEventTime = event?.eventTime;
      reopenDuration =
        _.toNumber(event?.duration) + getStudentExtraTime(events);
      totalReopenDuration = reopenDuration;
      startTimeAfterReopen = null;
      lastReopenEventTime = reopenEventTime;
    } else if (isEnd(event)) {
      // * END Event - resetting reopen state
      isReopened = false;
      reopenDuration = 0;
      reopenEventTime = null;
      startTimeAfterReopen = null;
    } else if (isReopened && isStart(event)) {
      // * START Event after REOPEN - recalculating reopen duration
      startTimeAfterReopen = event?.eventTime;
      const endTime = getIncrementedDate({
        key: "seconds",
        date: startTimeAfterReopen,
        incrementBy: reopenDuration + totalPausedDuration,
      });
      const compareDate = isWorksheetPaused ? pauseEventTime : getTodaysDate();
      reopenDuration = getDateTimeDifference({
        date1: endTime,
        date2: compareDate,
        differenceBy: "seconds",
      });
    }
  });

  return {
    isReopened,
    reopenDuration,
    lastReopenEventTime,
    reopenEventTime,
    totalReopenDuration,
    startTimeAfterReopen,
  };
};

// * Extracting Extra Data from Events
export const getStudentWorksheetDataFromEvents = (_events, data) => {
  let events = _.orderBy(_events, ["eventTime"], ["asc"]);
  const reopenEventIndex = _.findLastIndex(events, isReopen);
  if (reopenEventIndex !== -1) events = events.slice(reopenEventIndex);

  const pauseState = getStudentWorksheetPauseState(events, data);
  const reopenState = getStudentWorksheetReopenState(events, data);
  return {
    extraTime: getStudentExtraTime(events),
    isReopened: _.get(reopenState, "isReopened", false),
    endTime: _.get(_.find(events, isEnd), "eventTime", null),
    startTime: _.get(_.find(events, isStart), "eventTime", null),
    reopenDuration: _.get(reopenState, "reopenDuration", 0),
    totalPauseDuration: _.get(pauseState, "totalPausedDuration", 0),
    totalReopenDuration: _.get(reopenState, "totalReopenDuration", 0),
    currentPauseDuration: _.get(pauseState, "currentPausedDuration", 0),
  };
};

// * Calculates remaining duration when the test has not started yet.
const getWorksheetTimerStateWhenTestNotStarted = ({
  now,
  extraTime,
  isReopened,
  publishedAt,
  isFixedStartTime,
  isWorksheetPaused,
  timedTestDuration,
  totalPauseDuration,
  currentPauseDuration,
  closeSubmissionDate,
  totalReopenDuration,
}) => {
  // * If worksheet is reopened, return total reopen duration
  if (isReopened) return totalReopenDuration;

  const computedPauseDuration = isWorksheetPaused
    ? totalPauseDuration + currentPauseDuration
    : totalPauseDuration;

  // * Flexible Start Time Worksheet
  if (!isFixedStartTime) {
    const adjustedCloseDate = getIncrementedDate({
      key: "seconds",
      date: closeSubmissionDate,
      incrementBy: computedPauseDuration,
    });

    const closingWindow = getDateTimeDifference({
      date2: now,
      differenceBy: "seconds",
      date1: adjustedCloseDate,
    });

    return Math.min(timedTestDuration, closingWindow) + extraTime;
  }

  // * Fixed Start Time Worksheet - If scheduled start time is still in the future
  if (moment(publishedAt).isAfter(moment(now))) {
    return getDateTimeDifference({
      date1: now,
      date2: publishedAt,
      differenceBy: "seconds",
    });
  }

  // * Fixed Start Time Worksheet - Test start time has passed but student hasn't started yet
  const adjustedCloseDate = getIncrementedDate({
    key: "seconds",
    date: closeSubmissionDate,
    incrementBy: computedPauseDuration + extraTime,
  });

  return getDateTimeDifference({
    date2: now,
    differenceBy: "seconds",
    date1: adjustedCloseDate,
  });
};

// * Calculates remaining duration when the test has not started yet.
const getWorksheetTimerStateWhenTestStarted = ({
  now,
  startTime,
  extraTime,
  isReopened,
  isWorksheetPaused,
  submissionEndTime,
  timedTestDuration,
  totalPauseDuration,
  totalReopenDuration,
  closeSubmissionDate,
  currentPauseDuration,
}) => {
  // * If submission end time is defined, use it as the remaining duration
  if (!_.isEmpty(submissionEndTime) && !isWorksheetPaused) {
    return getDateTimeDifference({
      date2: now,
      differenceBy: "seconds",
      date1: submissionEndTime,
    });
  }

  const computedPauseDuration = totalPauseDuration + currentPauseDuration;

  // * Test is paused AND reopened
  if (isReopened) {
    const endTime = getIncrementedDate({
      key: "seconds",
      date: startTime,
      incrementBy: computedPauseDuration + totalReopenDuration,
    });
    return moment(endTime).diff(moment(now), "seconds");
  }

  // * Test is paused but NOT reopened -
  const testEndTime = getIncrementedDate({
    key: "seconds",
    date: startTime,
    incrementBy: timedTestDuration + computedPauseDuration,
  });
  const testDurationCap = getDateTimeDifference({
    date2: now,
    date1: testEndTime,
    differenceBy: "seconds",
  });

  const adjustedCloseDate = getIncrementedDate({
    key: "seconds",
    date: closeSubmissionDate,
    incrementBy: computedPauseDuration,
  });
  const closeDateCap = getDateTimeDifference({
    date2: now,
    differenceBy: "seconds",
    date1: adjustedCloseDate,
  });

  return Math.min(testDurationCap, closeDateCap) + extraTime;
};

// * Returns Timer state { remainingDuration, state, isReopened, isTestStarted, isTestEnded }
export const getWorksheetTimerState = ({
  events = [],
  publishedAt,
  isWorksheetPaused,
  submissionEndTime,
  closeSubmissionDate: _closeDate,
  isFixedStartTime: _isFixedStartTime,
  timedTestDuration: _timedTestDuration,
  getTotalDuration = false,
  isSubmitted = false,
}) => {
  const {
    endTime,
    startTime,
    extraTime,
    isReopened,
    totalPauseDuration,
    totalReopenDuration,
    currentPauseDuration,
  } = getStudentWorksheetDataFromEvents(events);

  const now = getTodaysDate();
  const closeSubmissionDate = _closeDate || moment().add(100, "years").toDate(); // Far future date for no close date
  const timedTestDuration = _.toNumber(_timedTestDuration || 0) * 60; // Convert minutes to seconds
  const isFixedStartTime = isReopened ? false : _isFixedStartTime; // If reopened, treat as flexible start time
  const isTestStarted = Boolean(startTime);
  const isTestEnded = Boolean(endTime) || isSubmitted;

  // * Get Total Test Duration, if reopen duration is > 0 return that else return timed test duration + extra time
  if (getTotalDuration) {
    if (totalReopenDuration > 0) return { totalDuration: totalReopenDuration };
    return { totalDuration: timedTestDuration + extraTime };
  }

  const common = { isReopened, isTestStarted, isTestEnded };
  const payload = {
    now,
    isReopened,
    isFixedStartTime,
    isWorksheetPaused,
    extraTime,
    startTime,
    publishedAt,
    submissionEndTime,
    timedTestDuration,
    totalPauseDuration,
    closeSubmissionDate,
    totalReopenDuration,
    currentPauseDuration,
  };

  // * CASE 1 : Test has not started yet
  if (!isTestStarted && !isTestEnded) {
    const remainingDuration = getWorksheetTimerStateWhenTestNotStarted(payload);
    const state = remainingDuration <= 0 ? "ENDED" : "NOT_STARTED";
    return { remainingDuration, state, ...common };
  }

  // * CASE 2: Test is currently in progress
  if (isTestStarted && !isTestEnded) {
    let state = isWorksheetPaused ? "PAUSED" : "STARTED";
    const remainingDuration = getWorksheetTimerStateWhenTestStarted(payload);
    if (remainingDuration <= 0) state = "ENDED";
    return { remainingDuration, state, ...common };
  }

  // * CASE 3: Test has ended
  return { remainingDuration: 0, state: "ENDED", ...common };
};
